import React, { useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@mantine/core';
import {
    Spotlight,
    type SpotlightActionData,
    type SpotlightActionGroupData,
    spotlight,
} from '@mantine/spotlight';
import { IconSearch } from '@tabler/icons-react';
import { useDocument } from '@/shared/hooks/useDocument';
import { useMenus, type MenuItemType } from '@/core/api/query/menus.query';
import { useAuthStore } from '@/core/stores/auth.store';

interface Params {
    type: string;
    docType: string;
    action?: string;
    docNo?: string;
    [key: string]: string | undefined;
}

/**
 * Labels for the ?view= tab used by list pages that host multiple views (e.g. Task
 * Master). Without this the trail reads identically on every tab.
 */
const VIEW_LABELS: Record<string, string> = {
    'sprint-dashboard': 'Dashboard',
    sprint: 'Sprint',
    backlog: 'Backlog',
    mywork: 'My Work',
    capacity: 'Capacity',
    stars: 'Stars',
    calendar: 'Calendar',
    board: 'Board',
};

interface BreadcrumbItem {
    title: string;
    href?: string;
    highlight?: boolean;
    isTitle?: boolean;
}

export function Breadcrumbs(): React.ReactElement {
    const { type, docType, docNo, action } = useParams<Params>();
    const { documentConfig } = useDocument(docType ?? '');
    const user = useAuthStore((state) => state.authState.user);
    const { data: menus = [] } = useMenus(user);
    const navigate = useNavigate();
    const location = useLocation();

    const handleNavigate = (href: string) => {
        navigate(href);
    };

    const breadcrumbItems = useMemo(() => {
        const firstPathSegment = location.pathname.split('/')[1];
        const isReportsPath = firstPathSegment === 'reports';
        const isMasterPath = type === 'master' || firstPathSegment === 'master';

        // The active view tab, for list pages that host several views under ?view=.
        const viewParam = new URLSearchParams(location.search).get('view');
        const viewTitle = !isReportsPath && !action && viewParam
            ? VIEW_LABELS[viewParam] ?? undefined
            : undefined;

        // Only the final crumb is the current location.
        const formNameIsLast = !viewTitle && !action && !docNo;

        const items: BreadcrumbItem[] = [
            // Named "Home", not "Dashboard" — a page's own Dashboard tab is a
            // different place, and using one word for both read as inconsistent.
            { title: 'Home', href: isReportsPath ? './dashboard' : '/' },
            ...(isMasterPath ? [{ title: 'Master' }] : []),
            ...(isReportsPath ? [{ title: 'Reports', href: '/reports' }] : []),
            ...(documentConfig?.formName
                ? [{
                    title: documentConfig.formName,
                    href: `/master/${docType}`,
                    highlight: formNameIsLast,
                    isTitle: true
                }]
                : []),
            ...(viewTitle ? [{ title: viewTitle, href: undefined, highlight: true }] : []),
            ...(!isReportsPath && action ? [{ title: action.charAt(0).toUpperCase() + action.slice(1), href: undefined }] : []),
            ...(!isReportsPath && docNo ? [{ title: docNo, href: undefined, highlight: true }] : []),
        ];

        return items;
    }, [location.pathname, location.search, documentConfig, docType, action, docNo, type]);

    const flattenMenus = useCallback(
        (items: MenuItemType[]): MenuItemType[] => {
            return items.flatMap((item) => {
                const withChildren = item.subMenu?.length
                    ? flattenMenus(item.subMenu)
                    : [];
                return [item, ...withChildren];
            });
        },
        [],
    );

    const spotlightActions = useMemo<
        (SpotlightActionGroupData | SpotlightActionData)[]
    >(() => {
        if (!menus || menus.length === 0) return [];

        const flatMenus = flattenMenus(menus);

        const menuActions: SpotlightActionData[] = flatMenus
            .filter((menu) => menu.url)
            .map((menu) => ({
                id: menu.url ?? `menu-${menu.id}`,
                label: menu.label,
                description: menu.url ?? undefined,
                onClick: () => {
                    if (menu.url) {
                        navigate(menu.url);
                    }
                },
                group: menu.isReport ? 'Reports' : 'Menus',
            }));

        const staticActions: SpotlightActionData[] = [
            {
                id: 'home',
                label: 'Home',
                description: 'Go to dashboard',
                onClick: () => navigate('/'),
                group: 'Pages',
            },
        ];

        return [...staticActions, ...menuActions];
    }, [menus, navigate, flattenMenus]);

    return (
        <div className="flex items-center justify-between w-full gap-3 app-breadcrumbs">
            <div className="flex-1 min-w-0">
                <ul className="flex items-center gap-1 min-w-0 app-breadcrumbs-trail">
                    {breadcrumbItems.map((item, index) => (
                        <li key={index} className="flex items-center min-w-0 app-breadcrumbs-item">
                            {index > 0 && (
                                <svg
                                    className="w-3.5 h-3.5 shrink-0 app-breadcrumbs-separator"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M0 0h24v24H0V0z" fill="none" />
                                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" />
                                </svg>
                            )}
                            {item.href ? (
                                <a
                                    href={item.href}
                                    className={`
                                        app-breadcrumbs-label
                                        ${item.isTitle ? 'app-breadcrumbs-root-title' : ''}
                                        ${item.highlight ? 'app-breadcrumbs-current' : ''}
                                    `}
                                    data-clickable="true"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (item.href) handleNavigate(item.href);
                                    }}
                                    title={item.title}
                                >
                                    {item.title}
                                </a>
                            ) : (
                                <span
                                    className={`
                                        app-breadcrumbs-label
                                        ${item.isTitle ? 'app-breadcrumbs-root-title' : ''}
                                        ${item.highlight ? 'app-breadcrumbs-current' : ''}
                                    `}
                                    title={item.title}
                                >
                                    {item.title}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="shrink-0">
                <Button
                    size="xs"
                    variant="default"
                    radius="xl"
                    leftSection={<IconSearch size={16} stroke={1.5} />}
                    onClick={spotlight.open}
                >
                    Search
                </Button>
            </div>

            <Spotlight
                actions={spotlightActions}
                nothingFound="Nothing found..."
                highlightQuery
                searchProps={{
                    leftSection: <IconSearch size={20} stroke={1.5} />,
                    placeholder: 'Search pages and menus...',
                }}
                maxHeight={350}
            />
        </div>
    );
}